/**
 * @genesis_domain Feedback & Loading States
 * @genesis_version V9.1
 * @genesis_intent Dual-Glass Protocol compliant loading spinner with Studio Equilibrium (mobile) and Luminous Frost (desktop) styling.
 * @genesis_boundaries This component is FORBIDDEN from containing business logic, API calls, or route-specific behavior.
 * @genesis_ai_trail V77.0: Created as part of GENESIS V77 Safety Net Audit. Implements V65 Dual-Glass Protocol with Surface Anchor gradient for optical grounding.
 */

import React from 'react';

export interface GlassLoadingSpinnerProps {
    /** Optional message to display below the spinner */
    message?: string;
    /** Size variant: 'sm' (24px), 'md' (48px), 'lg' (64px) */
    size?: 'sm' | 'md' | 'lg';
    /** Whether to show full-screen overlay (true) or inline spinner (false) */
    fullScreen?: boolean;
}

export const GlassLoadingSpinner: React.FC<GlassLoadingSpinnerProps> = ({
    message = 'Loading...',
    size = 'md',
    fullScreen = false,
}) => {
    const sizeClasses = {
        sm: 'h-6 w-6 border-2',
        md: 'h-12 w-12 border-3',
        lg: 'h-16 w-16 border-4',
    };

    const spinnerSize = sizeClasses[size];

    const content = (
        <div className="relative">
            {/* Surface Anchor Layer - Prevents "washed out" look */}
            <div className="absolute inset-0 z-[-1] bg-gradient-to-b from-transparent to-[var(--color-primary-base)]/10 pointer-events-none rounded-2xl" />

            {/* Mobile: Studio Equilibrium (Dark glass, inverted text) */}
            <div className="md:hidden flex flex-col items-center justify-center p-8 rounded-2xl theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] shadow-[0_0_25px_rgba(99,102,241,0.4),_inset_0_1px_0_rgba(255,255,255,0.6)]">
                <div className={`animate-spin rounded-full ${spinnerSize} border-b-2 border-t-transparent border-l-transparent border-r-transparent border-[var(--color-spinner)]`} />
                {message && (
                    <p className="mt-4 text-[var(--color-card-text)] font-sans text-sm font-medium tracking-tight">
                        {message}
                    </p>
                )}
            </div>

            {/* Desktop: Luminous Frost (Light glass, chromatic inversion) */}
            <div className="hidden md:flex flex-col items-center justify-center p-8 rounded-2xl theme-card backdrop-blur-3xl border border-[var(--color-glass-border)] shadow-[0_20px_50px_rgba(79,70,229,0.15)]">
                <div className={`animate-spin rounded-full ${spinnerSize} border-b-2 border-t-transparent border-l-transparent border-r-transparent border-[var(--color-spinner)]`} />
                {message && (
                    <p className="mt-4 text-[var(--color-card-text)] font-sans text-sm font-medium tracking-tight">
                        {message}
                    </p>
                )}
            </div>
        </div>
    );

    if (fullScreen) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 theme-modal-backdrop">
                {content}
            </div>
        );
    }

    return content;
};